import type {
	FollowingLineGeneratorResult,
	FollowingListItem,
	WorkerActivitySource,
} from '@/components/following/types';

export interface FollowingEvent {
	id: string;
	createdAt: Date;
	line: FollowingLineGeneratorResult;
	agencyId?: string;
	agencyName?: string;
	source: WorkerActivitySource;
	profileHistoryType?: string;
}

export interface FollowingSection {
	title: string;
	items: FollowingListItem[];
}

const formatSectionTitle = (date: Date): string =>
	date.toLocaleDateString('fr-FR', {
		day: 'numeric',
		month: 'short',
		year: '2-digit',
	});

/**
 * Groups a flat list of following events by calendar day (descending) and returns
 * one section per day, each section containing the events as FollowingListItem.
 */
export const groupEventsByDate = (
	events: FollowingEvent[]
): FollowingSection[] => {
	const grouped = new Map<string, { date: Date; items: FollowingListItem[] }>();

	for (const event of events) {
		const key = event.createdAt.toDateString();
		if (!grouped.has(key)) {
			grouped.set(key, { date: event.createdAt, items: [] });
		}
		// biome-ignore lint/style/noNonNullAssertion: Init Biome
		grouped.get(key)!.items.push({ id: event.id, ...event.line });
	}

	return Array.from(grouped.values())
		.sort((a, b) => b.date.getTime() - a.date.getTime())
		.map(({ date, items }) => ({ title: formatSectionTitle(date), items }));
};

/** Strips non-line fields from a FollowingListItem, keeping only FollowingLineProps. */
export const toFollowingLineProps = ({
	id: _id,
	content: _content,
	noteContext: _noteContext,
	interviewContext: _interviewContext,
	...lineProps
}: FollowingListItem) => lineProps;
